import { Avatar, AvatarFallback, AvatarImage } from "@/modern/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { X } from "lucide-react";
import { cn } from "@/modern/utils";

export interface BoardUser {
  id: number;
  name: string;
  profileImageURL?: string;
  archived?: boolean;
}

// Get initials for avatar fallback (single character)
export const getInitials = (name: string): string => {
  return name.charAt(0).toUpperCase();
};

// Helper function to generate consistent avatar colors based on name
export const getAvatarColor = (name: string): string => {
  const colors = [
    "bg-teal-600",
    "bg-blue-600",
    "bg-indigo-600",
    "bg-green-600",
    "bg-cyan-600",
    "bg-orange-600",
    "bg-purple-600",
    "bg-pink-600",
    "bg-red-600",
    "bg-violet-600",
  ];

  // Generate consistent color based on name
  const charSum = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return colors[charSum % colors.length];
};

interface UserAvatarProps {
  user: BoardUser;
  size?: "small" | "default" | "large";
  showName?: boolean;
  secondaryText?: string;
  showTooltip?: boolean;
  showRemove?: boolean;
  onRemove?: () => void;
  className?: string;
  avatarClassName?: string;
}

export const UserAvatar = ({
  user,
  size = "default",
  showName = false,
  secondaryText,
  showTooltip = false,
  showRemove = false,
  onRemove,
  className,
  avatarClassName,
}: UserAvatarProps) => {
  const avatarColor = getAvatarColor(user.name);

  const sizeClasses = {
    small: "h-7 w-7 text-xs",
    default: "h-9 w-9 text-sm",
    large: "h-8 w-8 text-xs",
  };

  const textSizeClasses = {
    small: "text-xs",
    default: "text-sm",
    large: "text-sm",
  };

  // Avatar component
  const avatarElement = (
    <div className={cn("relative group/avatar isolate", showRemove && "inline-block")}>
      <Avatar
        className={cn(
          sizeClasses[size],
          "flex-shrink-0",
          !user.profileImageURL && avatarColor,
          user.archived && "opacity-50",
          showRemove && "ring-2 ring-background",
          avatarClassName
        )}
      >
        {user.profileImageURL ? (
          <AvatarImage src={user.profileImageURL} alt={user.name} />
        ) : null}
        <AvatarFallback className={cn("text-white font-medium", avatarColor)}>
          {getInitials(user.name)}
        </AvatarFallback>
      </Avatar>
      {showRemove && onRemove && (
        <button
          onClick={onRemove}
          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground items-center justify-center text-xs hidden group-hover/avatar:flex hover:bg-destructive/90 transition-colors z-10"
          aria-label={`Remove ${user.name}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  // If showing name, wrap in container with name
  const contentWithName = showName ? (
    <div className={cn("flex items-center gap-3", className)}>
      {avatarElement}
      <div className="flex-1 min-w-0">
        <p className={cn(textSizeClasses[size], "font-normal text-foreground truncate")}>
          {user.name}
        </p>
        {secondaryText && (
          <p className="text-xs text-muted-foreground truncate">{secondaryText}</p>
        )}
        {!secondaryText && user.archived && (
          <p className="text-xs text-muted-foreground">Archived</p>
        )}
      </div>
    </div>
  ) : (
    <div className={className}>{avatarElement}</div>
  );

  // If showing tooltip, wrap in tooltip provider
  if (showTooltip) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            {contentWithName}
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">{user.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return contentWithName;
};
